var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { formDataInStorage, readDfStorage } from "./helperLib";
import { HAU_REFERRED_BY_LO, HAU_RETURNING_CUSTOMER, getHearAboutUsValue, parseAgentFieldValue, } from "./agentSearchUtils";
/** Default LTV ratio for cash-out borrowing limit: (homeValue × ratio / 100) − loanBalance.
 *  Override per-field via the CMS ["ltvRatio", customRatio] validation rule. */
export var DEFAULT_LTV_RATIO = 80;
export function parseUpstreamCashOutAmounts(formData) {
    var _a, _b, _c, _d;
    var homeValue = 0;
    var loanAmt = 0;
    for (var key in formData) {
        var field = formData[key];
        if (key.endsWith("homeValueAmt") || ((_a = field === null || field === void 0 ? void 0 : field.config) === null || _a === void 0 ? void 0 : _a.fieldId) === "homeValueAmt") {
            homeValue = parseCurrencyToInt((_b = field === null || field === void 0 ? void 0 : field.fieldValue) !== null && _b !== void 0 ? _b : "0");
        }
        if (key.endsWith("loanAmt_R") || ((_c = field === null || field === void 0 ? void 0 : field.config) === null || _c === void 0 ? void 0 : _c.fieldId) === "loanAmt_R") {
            loanAmt = parseCurrencyToInt((_d = field === null || field === void 0 ? void 0 : field.fieldValue) !== null && _d !== void 0 ? _d : "0");
        }
    }
    return { homeValue: homeValue, loanAmt: loanAmt };
}
export function getCashOutBorrowingLimit(homeValue, loanAmt, ltvRatio) {
    if (ltvRatio === void 0) { ltvRatio = DEFAULT_LTV_RATIO; }
    if (homeValue <= 0)
        return null;
    return Math.max(0, Math.floor(homeValue * (ltvRatio / 100) - loanAmt));
}
export function clampCashOutValueIfNeeded(currentValue, homeValue, loanAmt, ltvRatio) {
    if (ltvRatio === void 0) { ltvRatio = DEFAULT_LTV_RATIO; }
    var limit = getCashOutBorrowingLimit(homeValue, loanAmt, ltvRatio);
    if (limit === null) {
        return { clampedValue: currentValue, didClamp: false };
    }
    var currentNum = parseCurrencyToInt(currentValue);
    if (currentNum <= limit) {
        return { clampedValue: currentValue, didClamp: false };
    }
    return { clampedValue: formatCurrency(limit), didClamp: true };
}
function validateAgentSearchField(v, e) {
    if (e === void 0) { e = "Please select a loan officer from the list"; }
    var selection = parseAgentFieldValue(v);
    var hearAboutUs = getHearAboutUsValue();
    if (selection === null || selection === void 0 ? void 0 : selection.skipped) {
        return hearAboutUs === HAU_RETURNING_CUSTOMER ? "" : e;
    }
    if (selection === null || selection === void 0 ? void 0 : selection.agentGuid) {
        return "";
    }
    if (hearAboutUs === HAU_RETURNING_CUSTOMER) {
        return "";
    }
    if (hearAboutUs === HAU_REFERRED_BY_LO) {
        return e;
    }
    return "";
}
export var fnRegistry = {
    required: function (v, e) {
        if (e === void 0) { e = "Required"; }
        return (Boolean(v === null || v === void 0 ? void 0 : v.trim()) ? "" : e);
    },
    optional: function () { return ""; },
    min: function (v, n, e) {
        var num = parseCurrencyToInt(v);
        if (isNaN(num)) {
            var len = (v === null || v === void 0 ? void 0 : v.length) || 0;
            return len < n ? (e !== null && e !== void 0 ? e : "Min length: ".concat(n)) : "";
        }
        return num < n ? (e !== null && e !== void 0 ? e : "Min: ".concat(formatCurrency(n))) : "";
    },
    max: function (v, n, e) {
        var num = parseCurrencyToInt(v);
        if (isNaN(num)) {
            var len = (v === null || v === void 0 ? void 0 : v.length) || 0;
            return len > n ? (e !== null && e !== void 0 ? e : "Max length: ".concat(n)) : "";
        }
        return num > n ? (e !== null && e !== void 0 ? e : "Max: ".concat(formatCurrency(n))) : "";
    },
    range: function (v, min, max, e) {
        var num = parseCurrencyToInt(v);
        if (isNaN(num))
            return "";
        return num < min || num > max
            ? (e !== null && e !== void 0 ? e : "Range ".concat(formatCurrency(min), " - ").concat(formatCurrency(max)))
            : "";
    },
    minLength: function (v, n, e) {
        return v.length < n ? (e !== null && e !== void 0 ? e : "Min length: ".concat(n)) : "";
    },
    maxLength: function (v, n, e) {
        return v.length > n ? (e !== null && e !== void 0 ? e : "Max length: ".concat(n)) : "";
    },
    email: function (v, e) {
        if (e === void 0) { e = "Invalid email address"; }
        return !/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(v) && e;
    },
    pattern: function (v, r, e) {
        if (e === void 0) { e = "Invalid format"; }
        if (r[0] === "/" && r[r.length - 1] === "/")
            r = r.slice(1, -1);
        return !RegExp(r).test(v) && e;
    },
    name: function (v, e) {
        if (e === void 0) { e = "Please enter a valid name"; }
        var n = v.trim();
        return (n.length === 0 || n[0] === "-" || n.endsWith("-")) && e;
    },
    phone: function (v, e) {
        if (e === void 0) { e = "Please enter a valid phone number"; }
        var p = v.replace(/[()\-\s_]/g, "");
        return (p.length !== 10 || p.startsWith("1")) && e;
    },
    equal: function (f, v) { return readDfStorage(f) === v; },
    notEqual: function (f, v) { return !fnRegistry.equal(f, v); },
    agentSearch: validateAgentSearchField,
    // Cross-field validator: checks cashOutAmt against (homeValueAmt * ltvRatio) - loanAmt_R.
    // The ratio is supplied by the CMS via the validation rule ["ltvRatio", 0.80].
    // Returns an error string when the value exceeds the computed limit, "" otherwise.
    // Runs synchronously inside checkAll so isFieldValid reflects the correct isValid
    // state without any extra dispatch or timing workaround.
    ltvRatio: function (v, ratio) {
        if (ratio === void 0) { ratio = DEFAULT_LTV_RATIO; }
        var num = parseCurrencyToInt(v);
        if (num <= 0)
            return "";
        var _a = parseUpstreamCashOutAmounts(formDataInStorage()), homeValue = _a.homeValue, loanAmt = _a.loanAmt;
        var limit = getCashOutBorrowingLimit(homeValue, loanAmt, ratio);
        if (limit === null)
            return "";
        return num > limit
            ? "Requested Amount Exceeds Borrowing Limit of ".concat(formatCurrency(limit))
            : "";
    },
};
export var inputKeyCheck = {
    text: function (val) { return val === "" || /^[a-z ,.'-]+$/i.test(val); },
    number: function (val) { return val === "" || /^[0-9]+$/.test(val); },
};
export function formatCurrency(num) {
    if (isNaN(num))
        return "$0";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(num);
}
export function parseCurrencyToInt(str) {
    if (typeof str === "number")
        return Math.floor(str);
    if (typeof str !== "string")
        return 0;
    var numeric = str.replace(/[^\d]/g, "");
    return Number(numeric) || 0;
}
export function singleCheck(fnInConfig, value) {
    var func, args;
    if (Array.isArray(fnInConfig)) {
        func = fnInConfig[0], args = fnInConfig.slice(1);
        args = __spreadArray([value], args, true);
    }
    else {
        func = fnInConfig;
        args = [value];
    }
    if (fnRegistry[func] !== undefined)
        return fnRegistry[func].apply(fnRegistry, args);
    return "";
}
export function checkAll(fieldValidation, val) {
    if (val === void 0) { val = ""; }
    var value = val.toString();
    var isRequired = fieldValidation.some(function (rule) { return rule === "required" || (Array.isArray(rule) && rule[0] === "required"); });
    if (isRequired) {
        var reqd = singleCheck("required", value);
        if (reqd)
            return reqd;
    }
    // If field is optional and empty, skip all other validators
    if (!isRequired && value === "") {
        return "";
    }
    var normalized = fieldValidation.map(function (rule) {
        if (Array.isArray(rule) && rule.length === 2) {
            var name_1 = rule[0], param = rule[1];
            switch (name_1) {
                case "minLength":
                    return [name_1, param, "Min length: ".concat(param)];
                case "maxLength":
                    return [name_1, param, "Max length: ".concat(param)];
                case "min":
                    return [name_1, param, "Min: ".concat(param)];
                case "max":
                    return [name_1, param, "Max: ".concat(param)];
                default:
                    return rule;
            }
        }
        return rule;
    });
    for (var _i = 0, normalized_1 = normalized; _i < normalized_1.length; _i++) {
        var rule = normalized_1[_i];
        var ret = singleCheck(rule, value);
        if (ret) {
            return ret;
        }
    }
    return "";
}
export function logicFnCheck(fnInConfig) {
    if (!fnInConfig) {
        return true;
    }
    var func, args;
    if (Array.isArray(fnInConfig))
        func = fnInConfig[0], args = fnInConfig.slice(1);
    else
        func = fnInConfig;
    if (fnRegistry[func] !== undefined) {
        var result = fnRegistry[func].apply(fnRegistry, args);
        return Boolean(result);
    }
    return false;
}
